import { CloseCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { Button, Select } from 'antd';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import style from './index.scss';

const allFilters = [
  {
    itemName: 'customerId',
    itemLabel: 'Customer ID'
  },
  {
    itemName: 'customerName',
    itemLabel: 'Customer Name'
  },
  {
    itemName: 'segmentCode',
    itemLabel: 'Segment Name'
  },
  {
    itemName: 'clusterId',
    itemLabel: 'Cluster ID'
  },
  {
    itemName: 'riskShift',
    itemLabel: 'Risk Level Change'
  }
];

const getFilterItemSelectMode = (itemName) => {
  switch (itemName) {
    case 'customerId':
    case 'customerName':
      return 'tags';
    default:
      return 'multiple';
  }
};

const CustomerFilter = ({
  segmentListData,
  clusterListData,
  customerTypeListData,
  riskLevelChangeListData,
  filters,
  setFilters,
  handleClearButtonClick,
  handleApplyButtonClick
}) => {
  const [placeholderIndex, setPlaceholderIndex] = useState(1);

  const nonEmptyFilters = filters.filter(
    (f) => Array.isArray(f.values) && f.values.length > 0
  );

  const handleFilterLabelChange = (current, newValue) => {
    const newFilters = allFilters.filter((f) => f.itemName === newValue);
    if (newFilters.length == 1) {
      const newFilter = newFilters[0];
      setFilters((previous) =>
        previous.map((p) => {
          if (p.itemName === current) return { ...newFilter, values: [] };
          return p;
        })
      );
    }
  };

  const handleFilterItemChange = (current, newValue) => {
    setFilters((previous) =>
      previous.map((p) => {
        if (p.itemName === current) return { ...p, values: newValue.slice(-1) };
        return p;
      })
    );
  };

  const handleFilterRemove = (toRemoveItem) => {
    setFilters((previous) =>
      previous.filter((p) => p.itemName !== toRemoveItem)
    );
  };

  const handleAddNewFilter = () => {
    setFilters((previous) => [
      ...previous,
      { itemName: `placeholder-${placeholderIndex}` }
    ]);
    setPlaceholderIndex((previous) => previous + 1);
  };

  const getFilterOptions = (itemName) => {
    const getOptionsFromData = (raw) => (
      <>
        {raw.data.map((r) => (
          <Select.Option value={r.value} key={r.value}>
            {r.label}
          </Select.Option>
        ))}
      </>
    );
    switch (itemName) {
      case 'segmentCode':
        return getOptionsFromData(segmentListData);
      case 'clusterId':
        return getOptionsFromData(clusterListData);
      case 'customerType':
        return getOptionsFromData(customerTypeListData);
      case 'riskShift':
        return getOptionsFromData(riskLevelChangeListData);
      default:
        return <></>;
    }
  };

  const getFilterOptionsLoading = (itemName) => {
    switch (itemName) {
      case 'segmentCode':
        return segmentListData.isLoading;
      case 'clusterId':
        return clusterListData.isLoading;
      case 'customerType':
        return customerTypeListData.isLoading;
      case 'riskShift':
        return riskLevelChangeListData.isLoading;
      default:
        return false;
    }
  };

  return (
    <div>
      <div className={style.body}>
        <div className={style.filterContainer}>
          <div className={style.containerHeader}>
            <span>Filter</span>
            <span>Item</span>
          </div>
          <div className={style.containerBody}>
            {filters.map((filter, idx) => (
              <div className={style.filterRow} key={filter.itemName}>
                <Select
                  className={style.filterType}
                  size="small"
                  value={filter.itemLabel}
                  onChange={(value) =>
                    handleFilterLabelChange(filter.itemName, value)
                  }
                  placeholder="Please select">
                  {allFilters
                    .filter((af) =>
                      filters.every((f) => f.itemName !== af.itemName)
                    )
                    .map((f) => (
                      <Select.Option key={f.itemName} value={f.itemName}>
                        {f.itemLabel}
                      </Select.Option>
                    ))}
                </Select>
                <Select
                  className={style.filterItem}
                  size="small"
                  mode={getFilterItemSelectMode(filter.itemName)}
                  maxTagCount={2}
                  allowClear
                  filterOption={(input, option) =>
                    option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  value={filter.values}
                  onChange={(value) =>
                    handleFilterItemChange(filter.itemName, value)
                  }
                  placeholder="Please select"
                  loading={getFilterOptionsLoading(filter.itemName)}>
                  {getFilterOptions(filter.itemName)}
                </Select>
                <Button
                  className={style.filterRemove}
                  style={{ visibility: idx === 0 ? 'hidden' : 'unset' }}
                  size="small"
                  type="text"
                  icon={<CloseCircleOutlined style={{ color: '#DF6057' }} />}
                  onClick={() => handleFilterRemove(filter.itemName)}
                />
              </div>
            ))}
            <Button
              className={style.addNewFilter}
              style={{
                display: filters.length >= allFilters.length ? 'none' : 'unset'
              }}
              size="small"
              type="text"
              icon={<PlusCircleOutlined style={{ color: '#21B4BD' }} />}
              onClick={handleAddNewFilter}
            />
          </div>
        </div>
      </div>
      <div className={style.footer}>
        <Button
          className={style.clearButton}
          onClick={handleClearButtonClick}
          disabled={
            filters.length === 0 ||
            (filters.length === 1 &&
              filters[0].itemName.startsWith('placeholder'))
          }>
          Clear
        </Button>
        <Button
          className={style.applyButton}
          onClick={handleApplyButtonClick}
          disabled={!nonEmptyFilters.length}>
          Apply
        </Button>
      </div>
    </div>
  );
};

CustomerFilter.propTypes = {
  segmentListData: PropTypes.any,
  clusterListData: PropTypes.any,
  customerTypeListData: PropTypes.any,
  riskLevelChangeListData: PropTypes.any,
  filters: PropTypes.array,
  setFilters: PropTypes.func,
  handleClearButtonClick: PropTypes.func,
  handleApplyButtonClick: PropTypes.func
};

export default CustomerFilter;
